import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import axios, { HttpStatusCode } from "axios"
import { envConfig } from "config/envConfig"
import { useAuth } from "modules/auth/Auth"
import { ReactNode, useEffect } from "react"
import { useParams } from "react-router"
import { ORG_HEADER_KEY } from "utils/constants"
import { dayjs } from "utils/date"

export enum ErrorCodeEnum {
	QueryParameterNotFoundError = 1000,
	DataIdNotFoundError = 1001,
	UserIdNotFoundError = 1002,
	EnrollmentIdNotFoundError = 1003,
	OrganizationNotFoundError = 1004,
	PhoneNumberAlreadyExistsError = 1005,
	EmailAlreadyExistsError = 1006,
	InvalidPhoneNumber = 1007,
	LinearIssueNotFoundError = 1023,
	SupportTicketNotFoundError = 1024,
	LinearAttachmentNotFoundError = 1029,
}

export interface Cursor {
	id: string
	timestamp: number
	timestampFieldName: string
}

export interface InfinitivePage<T> {
	nextCursor: Cursor | undefined
	data: T[]
}

export interface InfiniteData<T> {
	pageParams: number[]
	pages: InfinitivePage<T>[]
}

export interface SuccessResponse<T> {
	data: T
	status: HttpStatusCode
	message: string
	success: boolean
}

export interface SuccessPageResponse<T, U = unknown> extends SuccessResponse<
	{
		limit: number
		page: number
		pages: number
		result: T[]
		total: number
	} & U
> {}

export interface SuccessCursorResponse<T> extends SuccessResponse<{
	items: T[]
	nextCursor?: Cursor
}> {}

export type ErrorResponse = {
	code: ErrorCodeEnum
	status: number
	message: string
	timestamp: string
	value: any
}

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
			refetchOnWindowFocus: false,
		},
	},
})

const httpClient = axios.create({
	baseURL: envConfig.baseApiUrl().length > 0 ? envConfig.baseApiUrl() : "/",
})

export const ApiProvider = ({ children }: { children: ReactNode }) => (
	<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
)

export function useHttpClient() {
	const { scopeId } = useParams()
	const { getToken } = useAuth()

	useEffect(() => {
		// Set up request interceptor
		const requestInterceptor = httpClient.interceptors.request.use(
			async (config) => {
				const token = await getToken()
				if (token) {
					config.headers.Authorization = `Bearer ${token}`
				}
				// Set org header if available
				if (scopeId && scopeId.length > 0) {
					config.headers[ORG_HEADER_KEY] = scopeId
				}

				config.headers["Accept-Timezone"] = dayjs.tz.guess()

				return config
			},
			(error) => {
				return Promise.reject(error)
			},
		)

		const responseInterceptor = httpClient.interceptors.response.use(
			(response) => response,
			(error) => {
				if (import.meta.env.STORYBOOK !== "true") {
					const { pathname } = window.location

					const isAuthPage = [
						"/login",
						"/resetPassword",
						"/session-expired",
					].includes(pathname)

					if (error.response?.status === 401 && !isAuthPage) {
						window.location.href = "/session-expired"
					}
				}
				return Promise.reject(error.response?.data ?? error)
			},
		)

		// Clean up interceptor
		return () => {
			httpClient.interceptors.request.eject(requestInterceptor)
			httpClient.interceptors.response.eject(responseInterceptor)
		}
	}, [getToken, scopeId])

	return { httpClient }
}
